import axios from "axios";
import { useEffect, useState } from "react";
import { XMLParser, XMLBuilder } from "fast-xml-parser";
import {
  Navbar,
  Nav,
  Container,
  Accordion,
  Form,
  Button,
  Row,
  Col,
} from "react-bootstrap";
import { toast } from "react-toastify";

const GeneratedQuestions = ({ id, onDataLoad }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [questionData, setQuestionData] = useState([]);
  const [loading, setLoading] = useState(true);

  const parser = new XMLParser({ ignoreAttributes: false });
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    format: true,
  });

  const url = `${process.env.REACT_APP_API_URL}`;
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({ id: `${id}` });
        const { data } = await axios.put(
          `${url}/video-finder-transcript-summary?${params.toString()}`,
          undefined,
          axiosConfig
        );
        const questionSet = data.data || [];

        const parsedSet = questionSet.map((item) => {
          const parsed = parser.parse(item.questions);
          const questionsArray = Array.isArray(parsed.quiz.question)
            ? parsed.quiz.question
            : [parsed.quiz.question];

          return {
            ...item,
            parsedQuestions: questionsArray.map((q) => ({
              ...q,
              editedText:
                q.questiontext?.text?.replace(/<\/?[^>]+(>|$)/g, "") || "",
              answers: (Array.isArray(q.answer)
                ? q.answer
                : q.answer
                ? [q.answer]
                : []
              ).map((a) => ({
                text: String(a.text ?? ""),
                feedback: String(a.feedback?.text ?? ""),
                fraction: a["@_fraction"] || "0",
              })),
            })),
          };
        });

        setQuestionData(parsedSet);
        onDataLoad?.(parsedSet);
        if (parsedSet.length > 0) {
          setSelectedType(parsedSet[0].content_type);
          setSelectedId(parsedSet[0].id);
        }
      } catch (error) {
        console.error("Error during download process:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleQuestionTextChange = (typeId, index, value) => {
    setQuestionData((prev) =>
      prev.map((item) =>
        item.content_type_id === typeId
          ? {
              ...item,
              parsedQuestions: item.parsedQuestions.map((q, i) =>
                i === index ? { ...q, editedText: value } : q
              ),
            }
          : item
      )
    );
  };

  const handleAnswerTextChange = (typeId, qIndex, aIndex, value) => {
    setQuestionData((prev) =>
      prev.map((item) =>
        item.content_type_id === typeId
          ? {
              ...item,
              parsedQuestions: item.parsedQuestions.map((q, qi) =>
                qi === qIndex
                  ? {
                      ...q,
                      answers: q.answers.map((a, ai) =>
                        ai === aIndex ? { ...a, text: value } : a
                      ),
                    }
                  : q
              ),
            }
          : item
      )
    );
  };

  const handleFeedbackTextChange = (typeId, qIndex, aIndex, value) => {
    setQuestionData((prev) =>
      prev.map((item) =>
        item.content_type_id === typeId
          ? {
              ...item,
              parsedQuestions: item.parsedQuestions.map((q, qi) =>
                qi === qIndex
                  ? {
                      ...q,
                      answers: q.answers.map((a, ai) =>
                        ai === aIndex ? { ...a, feedback: value } : a
                      ),
                    }
                  : q
              ),
            }
          : item
      )
    );
  };

  const handleDelete = (typeId, index) => {
    setQuestionData((prev) =>
      prev.map((item) =>
        item.content_type_id === typeId
          ? {
              ...item,
              parsedQuestions: item.parsedQuestions.filter(
                (_, i) => i !== index
              ),
            }
          : item
      )
    );
  };

  const handleSave = () => {
    const current = questionData.find(
      (item) => item.content_type === selectedType
    );
    if (!current) return;

    const convertedQuestions = current.parsedQuestions.map((q) => ({
      "@_type": q["@_type"] || "unknown",
      name: { text: q.name?.text || `Question` },
      questiontext: {
        "@_format": "html",
        text: `<![CDATA[<p>${q.editedText}</p>]]>`,
      },
      answer: q.answers.map((a) => ({
        "@_fraction": a.fraction || "0",
        text: a.text,
        feedback: {
          text: a.feedback,
        },
      })),
    }));

    const finalXml = builder.build({ quiz: { question: convertedQuestions } });
    axios
      .put(
        `${url}/scribe-bot-publish`,
        {
          id: selectedId,
          content: finalXml,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      )
      .then(({ data }) => {
        toast.success("Content saved successfully");
      })
      .catch((error) => {
        console.error("Error saving content:", error);
        toast.error("Error saving content");
      });
  };

  const currentData = questionData.find((q) => q.content_type === selectedType);

  const downloadItems = (type) => {
    const url = `${process.env.REACT_APP_API_URL}/scribe-bot`;
    const params = new URLSearchParams({
      ids: JSON.stringify([selectedId]),
      output_format: "xml",
    });
    axios
      .put(`${url}?${params.toString()}`, undefined, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then(({ data }) => {
        const fileName = data.data;
        const fileUrl = `https://lc-scribe-ai.s3.us-east-2.amazonaws.com/${fileName}`;
        fetch(fileUrl, { method: "GET" })
          .then((response) => response.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          });
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        toast.error("Error downloading file");
      });
  };

  if (loading) return <div className="mt-4">Loading...</div>;

  return (
    <>
      <Navbar
        expand="lg"
        style={{ backgroundColor: "white", border: "1px solid #ddd" }}
      >
        <Container>
          <Nav className="me-auto">
            {questionData.map((item) => (
              <Nav.Link
                key={item.content_type_id}
                active={item.content_type === selectedType}
                style={{
                  color:
                    item.content_type === selectedType ? "#1d3c6e" : "#999",
                }}
                onClick={() => {
                  setSelectedType(item.content_type);
                  setSelectedId(item.id);
                }}
              >
                {item.content_type}
              </Nav.Link>
            ))}
          </Nav>
          <div className="d-flex gap-2">
            <Button
              className="footer-close btn btn-primary"
              onClick={handleSave}
            >
              Save changes
            </Button>
            <Button
              className="footer-close btn btn-primary"
              onClick={downloadItems}
            >
              Download as XML
            </Button>
          </div>
        </Container>
      </Navbar>

      <Container className="mt-4">
        <h4>{selectedType || "Questions not found."}</h4>
        <Accordion>
          {currentData?.parsedQuestions.map((q, qIndex) => (
            <Accordion.Item eventKey={qIndex.toString()} key={qIndex}>
              <Accordion.Header>
                {q.name?.text || `Question ${qIndex + 1}`}
              </Accordion.Header>
              <Accordion.Body>
                <Form.Label>Question</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={q.editedText}
                  onChange={(e) =>
                    handleQuestionTextChange(
                      currentData.content_type_id,
                      qIndex,
                      e.target.value
                    )
                  }
                />

                {q.answers.length > 0 && <hr />}
                {q.answers.map((answer, aIndex) => (
                  <div key={aIndex} className="mt-3">
                    <Form.Group>
                      <Form.Label>Answer {aIndex + 1}</Form.Label>
                      <Form.Control
                        type="text"
                        value={answer.text}
                        onChange={(e) =>
                          handleAnswerTextChange(
                            currentData.content_type_id,
                            qIndex,
                            aIndex,
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mt-1">
                      <Form.Label>Feedback</Form.Label>
                      <Form.Control
                        type="text"
                        value={answer.feedback}
                        onChange={(e) =>
                          handleFeedbackTextChange(
                            currentData.content_type_id,
                            qIndex,
                            aIndex,
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </div>
                ))}

                <Row className="mt-3">
                  <Col>
                    <Button
                      variant="danger"
                      onClick={() =>
                        handleDelete(currentData.content_type_id, qIndex)
                      }
                    >
                      Delete Question
                    </Button>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Container>
    </>
  );
};

export default GeneratedQuestions;
